/**
 * The audio features Channel B classifies on.
 *
 * Everything below the `AudioAnalyser` class at the bottom is a pure function of
 * arrays — no AudioContext, no clock, no DOM — so each feature can be checked
 * against a signal whose answer is known in advance rather than demonstrated
 * into a microphone and agreed with. That matters more here than anywhere else
 * in the project: a pitch detector that is wrong by an octave still produces
 * confident, plausible numbers, and there is no way to see that on a meter.
 *
 * The five features, and what each is for:
 *
 *   flatness      geometric / arithmetic mean of the power spectrum. Tone-like
 *                 against noise-like, and the single best discriminator in the
 *                 project: a whistle sits near 0.05, a raspberry near 0.8.
 *   zcr           high for fricatives, low for anything voiced.
 *   f0            normalised autocorrelation, peak lag. Growl below 80 Hz,
 *                 whistle 800-3000.
 *   periodicity   the height of that peak. Growl is *aperiodic* at low pitch,
 *                 which is what separates it from a hummed note.
 *   attack        dB rise over 20 ms. A plosive burst is nothing else.
 *
 * Attack is computed from the worklet envelope rather than from these frames,
 * and `EnvelopeTracker` is where. See its comment — it is the one feature rAF
 * cannot see.
 */

import { config } from '../core/config.js';

const EPS = 1e-10; // as SPEC1.md specifies, before any log
const RMS_FLOOR = 1e-7; // -140 dB, so silence has a finite dB rather than -Infinity

/** dB from a linear amplitude, floored rather than allowed to reach -Infinity. */
export function toDb(rms) {
  return 20 * Math.log10(Math.max(rms, RMS_FLOOR));
}

/** Root mean square of a time-domain block. */
export function rmsOf(x) {
  if (!x.length) return 0;
  let sum = 0;
  for (let i = 0; i < x.length; i++) sum += x[i] * x[i];
  return Math.sqrt(sum / x.length);
}

/**
 * Zero-crossing rate, as a fraction of `config.audio.ZCR_REF_HZ`.
 *
 * The mean is removed first. A DC offset — which plenty of USB microphones
 * have — moves the whole waveform off the axis and can suppress crossings
 * almost entirely, so the feature would read a loud fricative as voiced.
 *
 * See ZCR_REF_HZ in config.js for why this is not normalised by Nyquist.
 *
 * @param {ArrayLike<number>} x
 * @param {number} sampleRate
 * @returns {number} 0..1
 */
export function zeroCrossingRate(x, sampleRate) {
  const n = x.length;
  if (n < 2) return 0;

  let mean = 0;
  for (let i = 0; i < n; i++) mean += x[i];
  mean /= n;

  let crossings = 0;
  let prev = x[0] - mean;
  for (let i = 1; i < n; i++) {
    const cur = x[i] - mean;
    // Strict sign change only. `>= 0` on both sides would count a run of exact
    // zeros — a muted input — as a crossing per sample, reading digital silence
    // as the most fricative sound possible.
    if ((prev < 0 && cur > 0) || (prev > 0 && cur < 0)) crossings++;
    prev = cur;
  }

  // Two crossings per cycle, so half the crossing rate is the dominant
  // frequency the waveform behaves like.
  const dominantHz = (crossings / (n - 1)) * sampleRate * 0.5;
  return Math.min(1, dominantHz / config.audio.ZCR_REF_HZ);
}

/**
 * Convert `AnalyserNode.getFloatFrequencyData` output (dB magnitudes) to linear
 * power, in place into `out`.
 *
 * @param {ArrayLike<number>} db
 * @param {Float64Array} out
 */
export function powerFromDb(db, out) {
  for (let i = 0; i < db.length; i++) {
    // dB here is 20*log10(magnitude), so power is 10^(dB/10).
    out[i] = 10 ** (db[i] / 10);
  }
  return out;
}

/**
 * Spectral flatness over `[lo, hi)` bins: geometric mean over arithmetic mean.
 *
 * Silence is perfectly flat — every bin sits on the same noise floor — so this
 * returns ~1 for a quiet room. That is arithmetically correct and useless as a
 * classification, which is why `AudioAnalyser` gates on level before reporting
 * any spectral feature at all.
 *
 * @param {ArrayLike<number>} power
 * @param {number} lo first bin, inclusive
 * @param {number} hi last bin, exclusive
 * @returns {number} 0..1
 */
export function spectralFlatness(power, lo = 0, hi = power.length) {
  const n = hi - lo;
  if (n <= 0) return 0;

  let logSum = 0;
  let sum = 0;
  for (let i = lo; i < hi; i++) {
    const p = power[i];
    logSum += Math.log(p + EPS);
    sum += p;
  }
  const geometric = Math.exp(logSum / n);
  const arithmetic = sum / n;
  return arithmetic > 0 ? Math.min(1, geometric / arithmetic) : 0;
}

/**
 * Power-weighted mean frequency over `[lo, hi)` bins, in Hz.
 *
 * @param {ArrayLike<number>} power
 * @param {number} binHz width of one bin
 */
export function spectralCentroid(power, binHz, lo = 0, hi = power.length) {
  let num = 0;
  let den = 0;
  for (let i = lo; i < hi; i++) {
    num += power[i] * i * binHz;
    den += power[i];
  }
  return den > 0 ? num / den : 0;
}

/**
 * Reused working arrays. Not state — nothing is read across calls — but it
 * keeps a 60 Hz analysis loop from allocating 16 kB a frame.
 */
let _y = new Float32Array(0);
let _energy = new Float64Array(0);

function scratch(n) {
  if (_y.length < n) {
    _y = new Float32Array(n);
    _energy = new Float64Array(n + 1);
  }
  return { y: _y, energy: _energy };
}

/**
 * f0 and periodicity, by normalised autocorrelation.
 *
 * Three things here are not optional, and each of them is an octave error or
 * worse when left out:
 *
 * **Normalisation.** Raw autocorrelation decays with lag simply because fewer
 * samples overlap, so the peak at the true period is always smaller than the
 * one before it. Dividing by the energy of both overlapping windows makes the
 * peak height a real correlation in -1..1, which is what `periodicity` means.
 *
 * **Local maxima, not the global one.** At the shortest lag in the search range
 * a low-pitched signal has barely changed, so correlation there is near 1 and a
 * global argmax reports 3 kHz for a 100 Hz note. Only interior peaks count.
 *
 * **The first tall peak, not the tallest.** A periodic signal correlates just as
 * well at two periods as at one, and rounding pushes the tallest peak to the
 * octave below about as often as not. Taking the earliest peak within 90% of the
 * tallest is the standard fix.
 *
 * The search runs coarse then refines, because the full 1-sample search is
 * roughly two million multiply-adds per frame and this runs on rAF.
 *
 * @param {ArrayLike<number>} x  time domain, any DC offset is removed here
 * @param {number} sampleRate
 * @param {object} [o]
 * @returns {{f0: number, periodicity: number}} f0 is 0 when nothing periodic was found
 */
export function estimateF0(x, sampleRate, o = {}) {
  const {
    min = config.audio.F0_MIN,
    max = config.audio.F0_MAX,
    coarse = config.audio.ACF_COARSE,
  } = o;

  const n = x.length;
  const minLag = Math.max(2, Math.floor(sampleRate / max));
  const maxLag = Math.min(n - 2, Math.ceil(sampleRate / min));
  if (maxLag <= minLag + 2) return { f0: 0, periodicity: 0 };

  const { y, energy } = scratch(n);

  let mean = 0;
  for (let i = 0; i < n; i++) mean += x[i];
  mean /= n;
  for (let i = 0; i < n; i++) y[i] = x[i] - mean;

  // energy[k] = sum of squares over [0, k), so any window's energy is a
  // subtraction rather than a loop.
  energy[0] = 0;
  for (let i = 0; i < n; i++) energy[i + 1] = energy[i] + y[i] * y[i];
  if (energy[n] <= 0) return { f0: 0, periodicity: 0 };

  /** Normalised correlation at one lag. */
  const ncc = (lag) => {
    const m = n - lag;
    let sum = 0;
    for (let i = 0; i < m; i++) sum += y[i] * y[i + lag];
    // Energy of [0, m) against energy of [lag, n) — the two windows actually
    // being compared, which is what makes this a correlation coefficient.
    const denom = Math.sqrt(energy[m] * (energy[n] - energy[lag]));
    return denom > 0 ? sum / denom : 0;
  };

  const step = Math.max(1, Math.floor(coarse));

  // Coarse pass: keep every interior local maximum, and the tallest of them.
  const lags = [];
  const values = [];
  let prev = ncc(minLag);
  let cur = ncc(minLag + step);
  let tallest = -1;
  for (let lag = minLag + step; lag + step <= maxLag; lag += step) {
    const next = ncc(lag + step);
    if (cur > prev && cur >= next) {
      lags.push(lag);
      values.push(cur);
      if (cur > tallest) tallest = cur;
    }
    prev = cur;
    cur = next;
  }
  if (!lags.length || tallest <= 0) return { f0: 0, periodicity: 0 };

  // Earliest peak within 90% of the tallest — the octave guard.
  let chosen = lags[lags.length - 1];
  for (let i = 0; i < lags.length; i++) {
    if (values[i] >= 0.9 * tallest) {
      chosen = lags[i];
      break;
    }
  }

  // Fine pass over the coarse peak's neighbourhood.
  let bestLag = chosen;
  let bestR = ncc(chosen);
  const lo = Math.max(minLag + 1, chosen - step);
  const hi = Math.min(maxLag - 1, chosen + step);
  for (let lag = lo; lag <= hi; lag++) {
    const r = ncc(lag);
    if (r > bestR) {
      bestR = r;
      bestLag = lag;
    }
  }
  if (bestR <= 0) return { f0: 0, periodicity: 0 };

  // Parabolic interpolation for a sub-sample lag. At 3 kHz the period is only
  // 16 samples, so a whole-sample lag quantises f0 into ~200 Hz steps and a
  // steady whistle would appear to wander.
  let lag = bestLag;
  if (bestLag > minLag && bestLag < maxLag) {
    const a = ncc(bestLag - 1);
    const b = bestR;
    const c = ncc(bestLag + 1);
    const denom = a - 2 * b + c;
    if (denom !== 0) {
      const shift = (0.5 * (a - c)) / denom;
      if (Math.abs(shift) <= 1) lag = bestLag + shift;
    }
  }

  return { f0: sampleRate / lag, periodicity: Math.max(0, Math.min(1, bestR)) };
}

/**
 * Attack, tracked at envelope rate.
 *
 * This is the one feature that cannot be computed in the rAF loop. A plosive
 * burst is 10-20 ms long; rAF fires every 16 ms. Sampling the level twice a
 * frame catches the rise roughly half the time, and the half it misses is the
 * half where the burst happened between frames — so a "pow" is detected or not
 * depending on the phase of the display refresh, which is not a property of the
 * sound.
 *
 * The worklet envelope arrives every 2.7 ms. So the rise is measured there, and
 * the *peak* rise since the renderer last looked is held for it to collect. The
 * renderer still runs at 60 Hz; it just stops being the thing that decides
 * whether a transient existed.
 */
export class EnvelopeTracker {
  /**
   * @param {object} [o]
   * @param {number} [o.windowMs]  dB rise is measured over this
   * @param {number} [o.rate]      envelope samples per second
   */
  constructor({ windowMs = config.audio.ATTACK_WINDOW, rate = 375, noiseFloor = null } = {}) {
    this.windowMs = windowMs;
    /** Supplies the clamp. Without one, the static backstop is used. */
    this.noiseFloor = noiseFloor;
    /** Most recent dB, floored. */
    this.db = config.audio.FEATURE_FLOOR_DB;
    /** dB rise from the quietest point in the window to now. */
    this.attack = 0;
    this._peak = null;
    this._ring = null;
    this._n = 0;
    this._i = 0;
    this.setRate(rate);
  }

  /** Envelope rate is only known once the AudioContext exists. */
  setRate(rate) {
    this.rate = rate;
    const size = Math.max(2, Math.ceil((this.windowMs / 1000) * rate));
    this._ring = new Float32Array(size).fill(this._floor());
    this._n = 0;
    this._i = 0;
  }

  /**
   * Levels are clamped to the noise gate before the rise is measured.
   *
   * Without this the feature measures the background's own roughness. A block
   * is 128 samples; the RMS of 128 samples of room noise varies by several dB
   * from block to block for no reason at all, and a rise measured against the
   * quietest of eight such blocks is several dB of nothing.
   *
   * The session that settled it: attack had a median of 11.5 dB during silence
   * and 9.7 dB during actual sound. As a plosive detector it was worse than
   * useless — it was *anti*-correlated with sound.
   *
   * The clamp is at the gate rather than the floor, because the roughness lives
   * *above* the floor: clamping at the floor itself leaves every one of those
   * meaningless few dB intact. Everything at or below the gate collapses to one
   * value, so background produces exactly zero attack and a burst is measured
   * from the top of the background rather than from inside it.
   */
  _floor() {
    return this.noiseFloor ? this.noiseFloor.gate : config.audio.FEATURE_FLOOR_DB;
  }

  /** @param {{t: number, rms: number}} sample */
  push(sample) {
    const floor = this._floor();
    const db = Math.max(toDb(sample.rms), floor);
    this._ring[this._i] = db;
    this._i = (this._i + 1) % this._ring.length;
    if (this._n < this._ring.length) this._n++;

    // Clamped again on the way out, not only on the way in. The gate moves, and
    // an entry stored under a lower gate would otherwise sit below the current
    // one and manufacture a rise out of the difference.
    let min = Number.POSITIVE_INFINITY;
    for (let k = 0; k < this._n; k++) {
      const v = Math.max(this._ring[k], floor);
      if (v < min) min = v;
    }

    this.db = db;
    this.attack = db - min;
    if (this._peak === null || this.attack > this._peak) this._peak = this.attack;
    return this.attack;
  }

  /** Largest rise since the last call, and reset. */
  takeAttack() {
    const v = this._peak === null ? this.attack : this._peak;
    this._peak = null;
    return v;
  }

  reset() {
    this.setRate(this.rate);
    this.db = this._floor();
    this.attack = 0;
    this._peak = null;
  }
}

/**
 * The rAF-rate half: an AnalyserNode in, an AudioFrame out.
 *
 * Nothing here touches the DOM — rule 3 of the architecture section. It writes
 * a frame, the renderer reads it.
 */
export class AudioAnalyser {
  /**
   * @param {AnalyserNode} node
   * @param {EnvelopeTracker} [tracker] supplies `attack`; without one it reads 0
   */
  constructor(node, tracker = null, noiseFloor = null) {
    this.node = node;
    this.tracker = tracker;
    this.noiseFloor = noiseFloor;
    this.sampleRate = node.context.sampleRate;

    this._time = new Float32Array(node.fftSize);
    this._db = new Float32Array(node.frequencyBinCount);
    this._power = new Float64Array(node.frequencyBinCount);

    this.binHz = this.sampleRate / node.fftSize;
    const bins = node.frequencyBinCount;
    // Bin 0 is DC and is never included: it is the microphone's offset, not a
    // sound, and it would dominate both flatness and centroid.
    this.loBin = Math.min(bins - 1, Math.max(1, Math.round(config.audio.BAND_LO / this.binHz)));
    this.hiBin = Math.max(
      this.loBin + 1,
      Math.min(bins, Math.round(config.audio.BAND_HI / this.binHz)),
    );
  }

  /**
   * One analysis frame.
   *
   * @param {number} now performance.now()
   * @returns {import('../core/types.js').AudioFrame}
   */
  frame(now) {
    this.node.getFloatTimeDomainData(this._time);

    const rms = rmsOf(this._time);
    const db = toDb(rms);
    const attack = this.tracker ? this.tracker.takeAttack() : 0;

    // Below the gate there is no sound to describe, only background — and
    // background is spectrally flat, aperiodic and full of zero crossings,
    // which is to say it looks exactly like a raspberry to every feature here.
    // Reporting zeros is the honest answer and keeps the meters still in a
    // quiet room.
    //
    // The gate is adaptive because the background is not a constant of nature.
    // A fixed -70 dB gated five frames out of sixteen hundred on a real
    // microphone; see noisefloor.js.
    const gate = this.noiseFloor ? this.noiseFloor.gate : config.audio.FEATURE_FLOOR_DB;
    if (db < gate) {
      return { t: now, rms, db, zcr: 0, flatness: 0, centroid: 0, f0: 0, periodicity: 0, attack };
    }

    this.node.getFloatFrequencyData(this._db);
    powerFromDb(this._db, this._power);

    const { f0, periodicity } = estimateF0(this._time, this.sampleRate);

    return {
      t: now,
      rms,
      db,
      zcr: zeroCrossingRate(this._time, this.sampleRate),
      flatness: spectralFlatness(this._power, this.loBin, this.hiBin),
      centroid: spectralCentroid(this._power, this.binHz, this.loBin, this.hiBin),
      f0,
      periodicity,
      attack,
    };
  }
}

export default AudioAnalyser;
